import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import collect from 'collect.js';
import { COUPON_TYPES, DropoffAction, ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { Order, OrderData } from '../../services/exports/Interfaces';
import { OrderStoreInterface, StoreInterface } from '../types';

const initialOrder: Partial<OrderData> = {
  method: ORDER_METHODS.PICKUP,
  customer_note: '',
  delivery_street_name: null,
  delivery_street_number: null,
  delivery_city: null,
  delivery_state: null,
  delivery_country: null,
  delivery_company_name: null,
  delivery_doorbell_name: null,
  delivery_zip_code: null,
  delivery_lat: null,
  delivery_lng: null,
  delivery_dropoff_action: DropoffAction.RingDoorBell,
  order_instructions: '',
  room_service_details: null,
  promo: '',
  coupon_details: null,
  tip: undefined,
  scheduled_for: null,
  line_items: [],
  marketing_consent: false,
};

const orderSlice = createSlice({
  name: REDUX_STORE_SLICES.ORDER,
  initialState: {
    cached_order: initialOrder,
    ongoing_orders: [],
  },
  reducers: {
    updateOrder: (state: OrderStoreInterface, action: PayloadAction<Partial<OrderData>>) => {
      state.cached_order = { ...state.cached_order, ...action.payload };
    },
    resetOrder: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      const keep = [
        'customer_first_name',
        'customer_last_name',
        'customer_email',
        'customer_phone_number',
        'delivery_street_name',
        'delivery_street_number',
        'delivery_city',
        'delivery_state',
        'delivery_country',
        'delivery_zip_code',
        'delivery_dropoff_action',
        'delivery_doorbell_name',
        'delivery_company_name',
        'delivery_lat',
        'delivery_lng',
        'customer_note',
        'is_receipt_required',
        'invoice_company_name',
        'invoice_first_name',
        'invoice_last_name',
        'invoice_address',
        'invoice_zip_code',
        'invoice_city',
        'invoice_tax_id',
        'invoice_email',
      ];

      const data = {
        ...initialOrder,
        ...collect(state.cached_order).only(keep).items,
        method: state.cached_order?.method ?? ORDER_METHODS.PICKUP,
      };

      state.cached_order = {
        ...data,
        ...collect(action.payload).filter((value, key) => !data[key]).items,
      };
    },
    resetScheduledTime: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      state.cached_order = {
        ...state.cached_order,
        scheduled_for: action?.payload?.scheduled_for ?? null,
      };
    },
    addOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<Order>) => {
      const index = collect(state.ongoing_orders).search((item) => item.id === action.payload.id);

      state.ongoing_orders = collect([...(state.ongoing_orders ?? [])])
        .when(
          index === false,
          (items) => items.prepend(action.payload),
          (items) => items.put(index, action.payload),
        )
        .toArray();
    },
    removeOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<number>) => {
      state.ongoing_orders = collect(state.ongoing_orders).where('id', '!=', action.payload).toArray();
    },
  },
});

export const props = (state: OrderStoreInterface) => state;
export const selectOrderMethod = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.method;
export const selectCachedOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order;
export const selectScheduledFor = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER].cached_order?.scheduled_for;
export const selectCouponType = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.coupon_type;
export const selectOngoingOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER]?.ongoing_orders?.[0];
export const selectHasCachedOrderPromoCode = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER]?.cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE;

export default orderSlice;
